import { Button, Card } from '@/components'
import { cn } from '@/lib/utils'
import type { ResultNode } from '@/types/util'
import { count, useFormWatch } from '@/utils'
import { getNodes, getPageInfo } from '@/utils/graphql'
import { useMutation, useQuery } from '@apollo/client'
import { Loader } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { VariablesOf } from 'gql.tada'
import { useReducer } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { MANAGE_ABSENCE_REQUESTS_MUTATION } from '../mutations'
import { ABSENCE_REQUESTS_QUERY } from '../queries'
import { AbsenceRequestCard } from './absence-request-card'
import type { AbsencesFiltersForm } from './absences-filters'

export type AbsenceRequestNode = ResultNode<
  typeof ABSENCE_REQUESTS_QUERY,
  'allAbsences'
>

type ActionsMap = Map<string, 'approve' | 'reject'>

function reduceActions(
  state: ActionsMap,
  action: { id: string; type: 'approve' | 'reject' | null } | 'clear'
) {
  if (action === 'clear') return new Map<string, 'approve' | 'reject'>()

  if (action.type === null) {
    const map = new Map(state)
    map.delete(action.id)
    return map
  }

  return new Map(state).set(action.id, action.type)
}

interface Props {
  filter: UseFormReturn<AbsencesFiltersForm>
}

export function AbsenceRequests({ filter }: Props) {
  const translations = useTranslate({
    loading: 'absences.requests.loading',

    showing: ['absences.requests.showing-n', { count: 0, total: 0 }],
    reset: 'absences.requests.reset',
    save: 'absences.requests.save',

    reject: 'absences.requests.reject',
    approve: 'absences.requests.approve',

    period: 'absences.requests.period',
    used: 'absences.requests.used',
    including: 'absences.requests.including-this',
  })

  const [actions, dispatchAction] = useReducer(
    reduceActions,
    new Map<string, 'approve' | 'reject'>()
  )

  const queryVariables: VariablesOf<typeof ABSENCE_REQUESTS_QUERY> = {
    first: 24,
    absenceType: useFormWatch(filter, 'absenceType') || undefined,
    users: useFormWatch(filter, 'users') || undefined,
    leaderGroup: useFormWatch(filter, 'leaderGroup') || undefined,
  }

  const {
    data: queryData,
    loading: queryLoading,
    error: queryError,
    fetchMore,
  } = useQuery(ABSENCE_REQUESTS_QUERY, {
    variables: queryVariables,
  })

  const [mutate, { loading: mutationLoading }] = useMutation(
    MANAGE_ABSENCE_REQUESTS_MUTATION,
    {
      awaitRefetchQueries: true,
      refetchQueries: ['AbsenceRequests'],
    }
  )

  const absences = getNodes(queryData?.allAbsences)
  const { hasNextPage, cursor } = getPageInfo(queryData?.allAbsences)

  const [infiniteRef] = useInfiniteScroll({
    loading: queryLoading,
    disabled: !!queryError,
    hasNextPage: !!queryData?.allAbsences.pageInfo.hasNextPage,
    rootMargin: '0px 0px 400px 0px',
    onLoadMore() {
      void fetchMore({
        variables: {
          ...queryVariables,
          after: cursor,
        },
      })
    },
  })

  function handleSubmit() {
    const [approve, reject] = actions.entries().reduce<[string[], string[]]>(
      (acc, [id, type]) => {
        if (type === 'approve') acc[0].push(id)
        else acc[1].push(id)

        return acc
      },
      [[], []]
    )

    void mutate({
      variables: {
        approve,
        reject,
      },
    }).then(() => {
      dispatchAction('clear')
    })
  }

  if (queryLoading)
    return (
      <div className="flex justify-center pt-10 text-2xl">
        <div className="flex items-center justify-center gap-4">
          <Loader.Spinner size={36} thickness={4} />

          <span>{translations.loading}</span>
        </div>
      </div>
    )

  const numApprove = count(actions.values(), 'approve')
  const numReject = count(actions.values(), 'reject')

  return (
    <div className="relative">
      <Card
        className="sticky top-0 z-10 p-0 mb-4"
        contentClassName="flex items-center justify-between py-0 px-4 h-16"
      >
        {mutationLoading ? (
          <div className="flex justify-center w-full">
            <Loader.Dots size={42} />
          </div>
        ) : (
          <>
            <div className="flex items-center">
              <span>
                {translations.showing({
                  count: absences.length,
                  total: queryData?.allAbsences.total ?? 0,
                })}
              </span>

              {!!numApprove && (
                <>
                  <span className="mx-4 text-gray-300">|</span>

                  <div className="rounded-md bg-green-100 px-2 py-1 text-sm text-green-800">
                    {translations.approve}: <strong>{numApprove}</strong>
                  </div>
                </>
              )}

              {!!numReject && (
                <>
                  <span className="mx-4 text-gray-300">|</span>

                  <div className="rounded-md bg-red-100 px-2 py-1 text-sm text-red-800">
                    {translations.reject}: <strong>{numReject}</strong>
                  </div>
                </>
              )}
            </div>

            {!!actions.size && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => dispatchAction('clear')}
                >
                  {translations.reset}
                </Button>

                <Button size="sm" onClick={handleSubmit}>
                  {translations.save}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      <div
        className={cn(
          'grid gap-4 grid-cols-[repeat(auto-fill,minmax(400px,1fr))]',
          mutationLoading && 'pointer-events-none'
        )}
      >
        {absences.map(absence => {
          const action = actions.get(absence.id) ?? null

          return (
            <AbsenceRequestCard
              key={absence.id}
              absence={absence}
              action={action}
              onApprove={() =>
                dispatchAction({
                  id: absence.id,
                  type: action === 'approve' ? null : 'approve',
                })
              }
              onReject={() =>
                dispatchAction({
                  id: absence.id,
                  type: action === 'reject' ? null : 'reject',
                })
              }
            />
          )
        })}
      </div>

      {hasNextPage && (
        <div ref={infiniteRef} className="flex justify-center pt-8">
          <Loader.Dots size={48} />
        </div>
      )}
    </div>
  )
}
